import type { ReactNode } from 'react'
import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  Heading4,
  Icon,
  IconVariant,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

const StageHeaderRail = withCitrusTheme(Box, (theme) => ({
  width: '32px',
  height: '32px',
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    width: '40px',
    height: '40px',
  },
}))

const StageCircle = withCitrusTheme(Box, (theme) => ({
  borderRadius: '999px',
  width: '32px',
  height: '32px',
  fontSize: '16px',
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    width: '40px',
    height: '40px',
    fontSize: '20px',
  },
}))

const StageCircleCompleted = withCitrusTheme(StageCircle, () => ({
  borderColor: '#615cff',
}))

const StageHeaderLineTop = withCitrusTheme(Box, (theme) => ({
  width: '1px',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundImage: 'linear-gradient(#615cff 60%, rgba(255, 255, 255, 0) 0%)',
  backgroundPosition: 'center',
  backgroundSize: '1px 7px',
  backgroundRepeat: 'repeat-y',
  bottom: 'calc(100% - 1px)',
  height: '10px',
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    backgroundSize: '1px 8px',
    height: '12px',
  },
}))

const StageHeaderLineBottom = withCitrusTheme(Box, (theme) => ({
  width: '1px',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundImage: 'linear-gradient(#615cff 60%, rgba(255, 255, 255, 0) 0%)',
  backgroundPosition: 'center',
  backgroundSize: '1px 7px',
  backgroundRepeat: 'repeat-y',
  top: 'calc(100% - 1px)',
  height: '10px',
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    backgroundSize: '1px 8px',
    height: '12px',
  },
}))

type StageMarkerProps = {
  completed?: boolean
  step: number
}

const StageMarker = ({ completed = false, step }: StageMarkerProps) => {
  const CircleComponent = completed ? StageCircleCompleted : StageCircle

  return (
    <CircleComponent
      bg={Color.White}
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      position={DisplayPosition.Relative}
      flexShrink={0}
      color={Color.NeutralGrey_900}
      style={{
        border: `1px solid ${completed ? '#615cff' : '#30363d'}`,
        fontFamily: 'Axiforma',
        lineHeight: 1,
        zIndex: 1,
      }}
    >
      {completed ? <Icon name="tick" size={5} variant={IconVariant.Actionable} /> : step}
    </CircleComponent>
  )
}

type SectionStageProps = {
  children: ReactNode
  completed?: boolean
  step: number
  title: string
  showLineAbove?: boolean
  showLineBelow?: boolean
}

export const SectionStage = ({
  children,
  completed = false,
  step,
  title,
  showLineAbove = false,
  showLineBelow = false,
}: SectionStageProps) => (
  <Box>
    <Box minWidth="0" style={{ flex: 1 }}>
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        minHeight="40px"
        style={{ gap: '12px', marginBottom: '16px' }}
      >
        <StageHeaderRail
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          position={DisplayPosition.Relative}
          flexShrink={0}
        >
          {showLineAbove && <StageHeaderLineTop />}
          <StageMarker completed={completed} step={step} />
          {showLineBelow && <StageHeaderLineBottom />}
        </StageHeaderRail>
        <Box minWidth="0">
          <Heading4>{title}</Heading4>
        </Box>
      </Box>
      {children}
    </Box>
  </Box>
)
